import React, { useEffect, useState } from 'react';
import { Drawer, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Loading from '../../../../components/loading/Loading';
import CustomButton from '../../../../components/buttons/CustomButton';
import CandidateRow from './CandidateRow';
import RejectedRow from './RejectedRow';
import { formatDayLabel, plural } from '../utils';
import styles from '../Recovery.module.css';

const PAPER_SX = { width: { xs: '100%', sm: 480 }, boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.08)' };

/**
 * Drawer "Pessoas para chamar" de um intervalo livre: a rodada (até 10, já
 * ordenada pela API), com os estados carregando, erro (tentar de novo) e vazio.
 * Embaixo, recolhida, a calibração: quem cabia no horário e ficou de fora, com
 * nota e motivo, para conferir os critérios.
 */
function CandidatesDrawer({ open, onClose, candidates, onRetry, onCall, onDismiss }) {
  const { status, interval, list, rejected, error } = candidates;
  const [showRejected, setShowRejected] = useState(false);
  useEffect(() => { setShowRejected(false); }, [interval]);
  // O card conta só quem pode receber; a lista também mostra quem não pode, com o motivo
  const callable = list.filter((c) => c.channel?.status === 'ok').length;

  function renderRejected() {
    if (status !== 'ready' || !rejected || rejected.total === 0) return null;
    const shown = rejected.list.length;
    return (
      <div className={styles.rejectedSection}>
        <button type="button" className={styles.rejectedToggle} onClick={() => setShowRejected((v) => !v)}>
          {showRejected ? 'Esconder' : 'Ver'} quem ficou de fora ({rejected.total})
        </button>
        {showRejected && (
          <>
            <span className={styles.rejectedHint}>
              {rejected.qualifiedTotal} {rejected.qualifiedTotal === 1 ? 'pessoa se qualifica' : 'pessoas se qualificam'} para este horário
              {' '}(a rodada mostra até 10). Abaixo, as {shown} notas mais altas de quem não entrou, com o motivo.
            </span>
            {rejected.list.map((candidate) => <RejectedRow key={candidate.patientId} candidate={candidate} />)}
          </>
        )}
      </div>
    );
  }

  function renderBody() {
    if (status === 'loading') return <div className={styles.loadingRow}><Loading /></div>;
    if (status === 'error') {
      return (
        <div className={styles.error}>
          <span>{error}</span>
          <CustomButton variant="outlined" style="primary" label="Tentar de novo" onClick={onRetry} />
        </div>
      );
    }
    if (list.length === 0) {
      return <div className={styles.empty}>Nenhuma pessoa elegível para esse horário.</div>;
    }
    return list.map((candidate) => (
      <CandidateRow key={candidate.patientId} candidate={candidate} onCall={onCall} onDismiss={onDismiss} />
    ));
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: PAPER_SX }}>
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <div>
            <span className={styles.drawerKicker}>Pessoas para chamar</span>
            {interval && (
              <>
                <h3 className={styles.drawerTitle}>
                  {formatDayLabel(interval.date)}, {interval.start} às {interval.end}
                </h3>
                <p className={styles.drawerSubtitle}>
                  {[interval.professionalName, `${interval.minutes} min`].filter(Boolean).join(' · ')}
                  {status === 'ready' && list.length > 0 && ` · ${plural(callable, 'pessoa', 'pessoas')} nesta rodada`}
                </p>
                {status === 'ready' && list.length > 0 && (
                  <p className={styles.drawerSubtitle}>Quem você chamar sai da lista e a próxima leva aparece.</p>
                )}
              </>
            )}
          </div>
          <Tooltip title="Fechar" arrow>
            <IconButton onClick={onClose} size="small" aria-label="Fechar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <div className={styles.drawerBody}>
          {renderBody()}
          {renderRejected()}
        </div>
      </div>
    </Drawer>
  );
}

export default CandidatesDrawer;
